import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { binService } from "../../services/binService";
import { subscribeBinUpdates } from "../../services/binSocket";
import { useAuth } from "../../contexts/AuthContext";
import { anonymousBinService } from "../../utils/anonymousBinService";
import {
  TrashIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

const Bin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [binData, setBinData] = useState({ 
    materials: [], 
    totalPrice: 0, 
    totalQuantity: 0,
    items: [] 
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [message, setMessage] = useState("");

  const fetchBin = async () => {
    try {
      const response = await binService.getUserBin();
      // Extract bin data from the API response
      const raw = response?.data?.bin || response?.bin || {
        materials: [],
        totalPrice: 0,
        totalQuantity: 0,
        items: [],
      };
      setBinData({
        ...raw,
        materials: raw.materials || [],
        validationStatus: !!raw.validationStatus,
        selectedAgent: raw.selectedAgent ?? null,
      });
    } catch (error) {
      console.error("Error fetching bin:", error);
      setMessage("Error loading bin data");
      // Set empty bin data on error
      setBinData({
        materials: [],
        totalPrice: 0,
        totalQuantity: 0,
        items: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Authenticated bin comes from backend; pre-login bin comes from localStorage.
    if (!user) {
      setBinData(anonymousBinService.getBin());
      setLoading(false);
      return undefined;
    }

    fetchBin();
    return undefined;
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;

    const unsubscribe = subscribeBinUpdates((bin) => {
      if (bin) {
        setBinData({
          materials: bin.materials || [],
          totalPrice: bin.totalPrice ?? 0,
          totalQuantity: bin.totalQuantity ?? 0,
          validationStatus: !!bin.validationStatus,
          selectedAgent: bin.selectedAgent ?? null,
          items: bin.items || [],
        });
      } else {
        setBinData({
          materials: [],
          totalPrice: 0,
          totalQuantity: 0,
          validationStatus: false,
          selectedAgent: null,
          items: [],
        });
      }
      if (typeof window !== "undefined" && window.refreshNavbarCounts) {
        window.refreshNavbarCounts();
      }
    });

    return unsubscribe;
  }, [user]);

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      await handleRemoveItem(itemId);
      return;
    }

    setUpdating((prev) => ({ ...prev, [itemId]: true }));
    setMessage("");

    try {
      if (!user) {
        const next = anonymousBinService.updateQuantity(itemId, newQuantity);
        setBinData(next);
      } else {
        await binService.updateBinItem(itemId, newQuantity);
        await fetchBin();
      }
      // Refresh navbar counts
      if (window.refreshNavbarCounts) {
        window.refreshNavbarCounts();
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
      setMessage("Error updating quantity");
    } finally {
      setUpdating((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleRemoveItem = async (itemId) => {
    setMessage("");

    try {
      if (!user) {
        const next = anonymousBinService.removeFromBin(itemId);
        setBinData(next);
      } else {
        await binService.removeFromBin(itemId);
        await fetchBin();
      }
      setMessage("Item removed from bin");
      // Refresh navbar counts
      if (window.refreshNavbarCounts) {
        window.refreshNavbarCounts();
      }
    } catch (error) {
      console.error("Error removing item:", error);
      setMessage("Error removing item from bin");
    }
  };

  const handleClearBin = async () => {
    if (!window.confirm("Are you sure you want to clear your entire bin?")) {
      return;
    }

    setMessage("");

    try {
      if (!user) {
        anonymousBinService.clearBin();
        setBinData(anonymousBinService.getBin());
      } else {
        await binService.clearBin();
        await fetchBin();
      }
      setMessage("Bin cleared successfully");
      // Refresh navbar counts
      if (window.refreshNavbarCounts) {
        window.refreshNavbarCounts();
      }
    } catch (error) {
      console.error("Error clearing bin:", error);
      setMessage("Error clearing bin");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const binReadOnly = Boolean(user && binData.validationStatus);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">My Bin</h1>
        <p className="text-gray-600">
          Review your selected materials before connecting with an agent.
        </p>
        {binReadOnly && (
          <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            This bin has been validated. You cannot change quantities or remove
            items here. To add materials, use the materials list or a material
            detail page; you will be asked to confirm because that will send the
            bin back for agent validation.
          </p>
        )}
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.includes("successfully") || message.includes("removed")
              ? "bg-green-100 text-green-700 border border-green-200"
              : "bg-red-100 text-red-700 border border-red-200"
          }`}
        >
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            {message}
          </div>
        </div>
      )}

      {/* Empty Bin */}
      {!binData.materials?.length ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <ShoppingCartIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Your bin is empty
          </h2>
          <p className="text-gray-600 mb-6">
            Start by browsing our materials and adding them to your bin.
          </p>
          <Link
            to="/materials"
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
          >
            Browse Materials
          </Link>
        </div>
      ) : (
        <>
          {/* Bin Items */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Items ({binData?.materials?.length || 0})
              </h2>
            </div>

            <div className="divide-y divide-gray-200">
              {binData?.materials?.map((item) => (
                <div key={item._id} className="p-6">
                  <div className="flex items-center space-x-4">
                    {/* Material Image */}
                    <div className="flex-shrink-0">
                      {item.material?.image ? (
                        <img
                          src={item.material.image}
                          alt={item.material.name}
                          className="h-16 w-16 object-cover rounded-md"
                        />
                      ) : (
                        <div className="h-16 w-16 bg-gray-200 rounded-md flex items-center justify-center">
                          <span className="text-gray-500 text-xs">
                            No Image
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Material Details */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {item.material?.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Category:{" "}
                        {item.material?.category
                          ?.replace("_", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </p>
                      <p className="text-sm text-gray-600">
                        Price: ₦
                        {item.material?.pricePerKg?.toFixed(2) || "0.00"}/kg
                      </p>
                    </div>

                    {/* Quantity (editable only before validation) */}
                    <div className="flex items-center space-x-2">
                      {binReadOnly ? (
                        <span className="text-lg font-medium text-gray-900 min-w-[3rem] text-center">
                          {item.quantity} kg
                        </span>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              handleQuantityChange(item._id, item.quantity - 1)
                            }
                            disabled={updating[item._id]}
                            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded disabled:opacity-50"
                          >
                            -
                          </button>
                          <span className="text-lg font-medium text-gray-900 min-w-[3rem] text-center">
                            {updating[item._id] ? "..." : item.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              handleQuantityChange(item._id, item.quantity + 1)
                            }
                            disabled={updating[item._id]}
                            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded disabled:opacity-50"
                          >
                            +
                          </button>
                        </>
                      )}
                    </div>

                    {/* Item Price */}
                    <div className="text-right">
                      <p className="text-lg font-semibold text-gray-900">
                        ₦
                        {(
                          item.price != null
                            ? item.price
                            : (item.material?.pricePerKg || 0) * item.quantity
                        ).toFixed(2)}
                      </p>
                    </div>

                    {/* Remove Button */}
                    {!binReadOnly && (
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(item.material._id)}
                        className="text-red-600 hover:text-red-800 p-2"
                        title="Remove item"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Bin Summary */}
            <div className="bg-gray-50 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    Total: ₦
                    {binData?.totalPrice?.toFixed(2) || "0.00"}
                  </p>
                  <p className="text-sm text-gray-600">
                    {binData?.materials?.length || 0} item(s)
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {binData?.validationStatus && (
                    <button
                      onClick={() => navigate("/sell")}
                      className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      Proceed to Sell
                    </button>
                  )}
                  {!binData?.validationStatus && (
                    <>
                      <button
                        type="button"
                        onClick={handleClearBin}
                        className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                      >
                        Clear Bin
                      </button>
                      <Link
                        to="/agents"
                        className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors inline-flex items-center"
                      >
                        <UserGroupIcon className="h-5 w-5 mr-2" />
                        Get an Agent
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              Next Steps
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-blue-800">
              <li>Review your selected materials and quantities above</li>
              <li>
                Click "Get an Agent" to find a collection agent in your area
              </li>
              <li>
                The agent will validate your materials and set accurate
                quantities
              </li>
              <li>
                Once validated, you can complete your sale and receive payment
              </li>
            </ol>
          </div>
        </>
      )}
    </div>
  );
};

export default Bin;
